import React from 'react';
import type { ReviewHistoryItem } from '../../types/review.types';
import type { PaginationMeta } from '../../types/vocabulary.types';
import { Button } from '../common/Button';

interface ReviewHistoryTableProps {
  items: ReviewHistoryItem[];
  pagination: PaginationMeta;
  isLoading: boolean;
  onPageChange: (page: number) => void;
}

export const ReviewHistoryTable: React.FC<ReviewHistoryTableProps> = ({
  items,
  pagination,
  isLoading,
  onPageChange,
}) => {
  const formatDateTime = (isoString: string) => {
    try {
      const d = new Date(isoString);
      return d.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return isoString;
    }
  };

  const getRatingBadge = (item: ReviewHistoryItem) => {
    if (item.action === 'REVIEW' && item.rating) {
      switch (item.rating) {
        case 'AGAIN':
          return (
            <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-rose-500/20 text-rose-300 border border-rose-500/30">
              Again
            </span>
          );
        case 'HARD':
          return (
            <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-500/20 text-amber-300 border border-amber-500/30">
              Hard
            </span>
          );
        case 'GOOD':
          return (
            <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
              Good
            </span>
          );
        case 'EASY':
          return (
            <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-sky-500/20 text-sky-300 border border-sky-500/30">
              Easy
            </span>
          );
      }
    }

    if (item.action === 'LEARNED') {
      return (
        <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
          Marked Learned
        </span>
      );
    }

    if (item.action === 'UNLEARNED') {
      return (
        <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-700/50 text-slate-300 border border-slate-600/30">
          Marked To Learn
        </span>
      );
    }

    return (
      <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-surface-700 text-slate-300">
        {item.action}
      </span>
    );
  };

  if (items.length === 0 && !isLoading) {
    return (
      <div className="text-center py-16 bg-surface-800/40 rounded-2xl border border-white/5 p-8">
        <svg
          className="w-12 h-12 mx-auto text-slate-600 mb-3"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <h3 className="text-base font-semibold text-white">No Learning History Yet</h3>
        <p className="text-xs text-slate-400 mt-1 max-w-sm mx-auto">
          Start reviewing cards or study in flashcards to see your learning timeline recorded here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Desktop / Tablet Table View */}
      <div className="overflow-hidden rounded-2xl border border-white/10 bg-surface-800/40 backdrop-blur-sm">
        <table className="w-full text-left text-sm text-slate-300">
          <thead className="bg-surface-800/80 text-xs uppercase font-semibold text-slate-400 border-b border-white/10">
            <tr>
              <th scope="col" className="px-5 py-3.5">
                Word
              </th>
              <th scope="col" className="px-5 py-3.5 hidden sm:table-cell">
                Vietnamese Meaning
              </th>
              <th scope="col" className="px-5 py-3.5">
                Action / Result
              </th>
              <th scope="col" className="px-5 py-3.5 text-right">
                Latest Activity
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5 font-normal">
            {items.map((item) => (
              <tr
                key={item.id}
                className="hover:bg-white/[0.02] transition-colors"
                id={`history-row-${item.id}`}
              >
                <td className="px-5 py-3.5">
                  <span className="font-bold text-white text-base block">{item.word}</span>
                  <span className="text-xs text-emerald-400 sm:hidden block mt-0.5">
                    {item.meaningVi}
                  </span>
                </td>
                <td className="px-5 py-3.5 text-slate-300 hidden sm:table-cell">
                  {item.meaningVi}
                </td>
                <td className="px-5 py-3.5">{getRatingBadge(item)}</td>
                <td className="px-5 py-3.5 text-right text-xs text-slate-400 whitespace-nowrap font-mono">
                  {formatDateTime(item.reviewedAt)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between pt-2 px-1">
          <span className="text-xs text-slate-400 font-medium">
            Page {pagination.page} of {pagination.totalPages} ({pagination.total} words)
          </span>
          <div className="flex items-center gap-2">
            <Button
              id="history-prev-page-btn"
              variant="secondary"
              size="sm"
              disabled={pagination.page <= 1}
              onClick={() => onPageChange(pagination.page - 1)}
            >
              Previous
            </Button>
            <Button
              id="history-next-page-btn"
              variant="secondary"
              size="sm"
              disabled={pagination.page >= pagination.totalPages}
              onClick={() => onPageChange(pagination.page + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
